import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { API_URL } from '../config';
import './Conferences.css';

const Conferences = () => {
  const [showHistory, setShowHistory] = useState(false);
  const [showScholarshipForm, setShowScholarshipForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [updates, setUpdates] = useState([]);
  const [updatesLoaded, setUpdatesLoaded] = useState(false);
  const [scholarshipSettings, setScholarshipSettings] = useState({
    scholarshipApplicationsClosed: false,
    scholarshipClosedMessage: 'Submissions are closed for this year. Thank you to everyone who applied!'
  });

  useEffect(() => {
    fetch(`${API_URL}/api/conference-updates`)
      .then(res => res.ok ? res.json() : [])
      .then(data => setUpdates(Array.isArray(data) ? data : []))
      .catch(() => setUpdates([]))
      .finally(() => setUpdatesLoaded(true));

    fetch(`${API_URL}/api/conference-settings`)
      .then(res => res.ok ? res.json() : null)
      .then(data => {
        if (data) {
          setScholarshipSettings({
            scholarshipApplicationsClosed: !!data.scholarshipApplicationsClosed,
            scholarshipClosedMessage: data.scholarshipClosedMessage || 'Submissions are closed for this year. Thank you to everyone who applied!'
          });
        }
      })
      .catch(() => { /* keep defaults */ });
  }, []);

  const formatUpdateDate = (iso) => {
    if (!iso) return '';
    return new Date(iso).toLocaleDateString('en-GB', { year: 'numeric', month: 'long', day: 'numeric' });
  };
  const [scholarshipForm, setScholarshipForm] = useState({
    // 1. Personal Details
    fullName: '',
    email: '',
    country: '',
    // 2. Professional Context
    workPlace: '',
    position: '',
    areaOfPractice: '',
    // 3. Reasons for Applying
    motivation: '',
    // 4. Declaration
    confirmAccurate: false,
    understandNoGuarantee: false,
    agreeToFeedback: false
  });

  const handleScholarshipChange = (e) => {
    const { name, value, type, checked } = e.target;
    setScholarshipForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleScholarshipSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const response = await fetch(`${API_URL}/api/scholarship-application`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(scholarshipForm),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setScholarshipForm({
          fullName: '',
          email: '',
          country: '',
          workPlace: '',
          position: '',
          areaOfPractice: '',
          motivation: '',
          confirmAccurate: false,
          understandNoGuarantee: false,
          agreeToFeedback: false
        });
        setTimeout(() => {
          setShowScholarshipForm(false);
          setSubmitStatus(null);
        }, 3000);
      } else {
        const error = await response.json();
        setSubmitStatus('error');
        console.error('Submission error:', error);
      }
    } catch (error) {
      console.error('Submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="research-page">
      <div className="container">
        <h1 className="page-title">Conferences</h1>
        <p className="page-subtitle">Annual conferences bringing together solution-focused professionals from across Europe and beyond</p>

        {/* Latest Updates */}
        {updatesLoaded && updates.length > 0 && (
          <div style={{
            background: '#ffffff',
            borderRadius: '16px',
            padding: '1.75rem 2rem',
            marginBottom: '2.5rem',
            border: '1px solid #e2e8f0',
            boxShadow: '0 4px 10px -6px rgba(15, 23, 42, 0.08)'
          }}>
            <div style={{display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.25rem'}}>
              <div style={{fontSize: '1.5rem'}}>📣</div>
              <h2 style={{margin: 0, color: '#1e293b', fontSize: '1.4rem', fontWeight: 700}}>Latest Updates</h2>
            </div>
            <div style={{display: 'flex', flexDirection: 'column', gap: '1rem'}}>
              {updates.map(u => (
                <div key={u._id} style={{
                  padding: '1rem 1.25rem',
                  background: u.pinned ? '#fff7ed' : '#f8fafc',
                  borderRadius: '10px',
                  borderLeft: u.pinned ? '4px solid #f59e0b' : '4px solid #2563eb'
                }}>
                  <div style={{display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.35rem', flexWrap: 'wrap'}}>
                    {u.pinned && <span style={{fontSize: '0.85rem'}}>📌</span>}
                    <h3 style={{margin: 0, color: '#1e293b', fontSize: '1.1rem', fontWeight: 600}}>{u.title}</h3>
                    <span style={{color: '#94a3b8', fontSize: '0.85rem', marginLeft: 'auto'}}>{formatUpdateDate(u.publishDate)}</span>
                  </div>
                  {u.body && (
                    <div
                      className="conf-update-body"
                      style={{color: '#475569', fontSize: '0.95rem'}}
                      dangerouslySetInnerHTML={{__html: u.body}}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Current Conference Status */}
        <div style={{
          background: 'linear-gradient(135deg, #f0f9ff 0%, #dbeafe 100%)',
          borderRadius: '16px',
          padding: '2rem',
          marginBottom: '3rem',
          border: '1px solid #3b82f6'
        }}>
          <div style={{display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem'}}>
            <div style={{fontSize: '2.5rem'}}>🎯</div>
            <div>
              <h2 style={{margin: 0, color: '#1e293b', fontSize: '1.8rem'}}>2026 EBTA Conference</h2>
              <p style={{margin: '0.5rem 0 0 0', color: '#1e40af', fontWeight: '600'}}>Olomouc, Czech Republic</p>
            </div>
          </div>
          <div style={{marginBottom: '1.5rem'}}>
            <div style={{display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.5rem'}}>
              <strong style={{color: '#1e40af'}}>📍 Where:</strong>
              <span style={{color: '#475569'}}>Olomouc, Czech Republic</span>
            </div>
            <div style={{display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.5rem'}}>
              <strong style={{color: '#1e40af'}}>📅 When:</strong>
              <span style={{color: '#475569'}}>18-20 September 2026</span>
            </div>
            <div style={{display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem'}}>
              <strong style={{color: '#1e40af'}}>🎨 Theme:</strong>
              <span style={{color: '#475569', fontStyle: 'italic'}}>"PULS OF CHANGE"</span>
            </div>
          </div>
          <div style={{
            background: '#fef3c7',
            padding: '1rem 1.5rem',
            borderRadius: '8px',
            border: '1px solid #fde047'
          }}>
            <div style={{marginBottom: '0.5rem'}}>
              <strong style={{color: '#92400e'}}>📢 THE CONFERENCE WEBSITE IS LIVE!</strong>
              <span style={{color: '#92400e', marginLeft: '0.5rem'}}>Explore important details and deadlines for workshop proposal submissions and registration.</span>
            </div>
            <a
              href="https://ebta2026.cz/en/"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'inline-block',
                marginTop: '0.5rem',
                padding: '0.5rem 1rem',
                background: '#92400e',
                color: 'white',
                borderRadius: '6px',
                textDecoration: 'none',
                fontWeight: '600',
                fontSize: '0.9rem',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => e.target.style.background = '#78350f'}
              onMouseLeave={(e) => e.target.style.background = '#92400e'}
            >
              Visit Conference Website →
            </a>
          </div>
        </div>

        {/* Conference Scholarship Section */}
        <div style={{
          background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
          borderRadius: '16px',
          padding: '2rem',
          marginBottom: '3rem',
          border: '1px solid #059669'
        }}>
          <div style={{display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem'}}>
            <div style={{fontSize: '2.5rem'}}>🎓</div>
            <div>
              <h2 style={{margin: 0, color: '#166534', fontSize: '1.8rem'}}>EBTA Conference Scholarship</h2>
              <p style={{margin: '0.5rem 0 0 0', color: '#059669', fontWeight: '600'}}>€300 per scholarship</p>
            </div>
          </div>

          <div style={{marginBottom: '1.5rem'}}>
            <div style={{
              background: '#ffffff',
              padding: '1.5rem',
              borderRadius: '12px',
              marginBottom: '1.5rem',
              border: '1px solid #bbf7d0'
            }}>
              <p style={{color: '#475569', lineHeight: '1.8', marginBottom: '1rem'}}>
                The European Brief Therapy Association (EBTA) offers a limited number of Conference Scholarships to support participation in the EBTA Conference.
              </p>
              <p style={{color: '#475569', lineHeight: '1.8', marginBottom: '1rem'}}>
                These scholarships aim to encourage inclusion, professional development, and the ongoing growth of Solution Focused approach and practice across diverse contexts and regions with limited opportunities to learn from and engage with Solution Focused community.
              </p>

              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                gap: '1rem',
                marginBottom: '1rem'
              }}>
                <div style={{background: '#f0fdf4', padding: '0.85rem 1rem', borderRadius: '8px', border: '1px solid #bbf7d0'}}>
                  <div style={{fontSize: '0.8rem', color: '#059669', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.25rem'}}>Amount</div>
                  <div style={{color: '#166534', fontWeight: '700', fontSize: '1.1rem'}}>€300</div>
                </div>
                <div style={{background: '#f0fdf4', padding: '0.85rem 1rem', borderRadius: '8px', border: '1px solid #bbf7d0'}}>
                  <div style={{fontSize: '0.8rem', color: '#059669', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.25rem'}}>Deadline</div>
                  <div style={{color: '#166534', fontWeight: '700', fontSize: '1.1rem'}}>31 May</div>
                </div>
                <div style={{background: '#f0fdf4', padding: '0.85rem 1rem', borderRadius: '8px', border: '1px solid #bbf7d0'}}>
                  <div style={{fontSize: '0.8rem', color: '#059669', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.25rem'}}>Eligibility</div>
                  <div style={{color: '#166534', fontWeight: '700', fontSize: '1.1rem'}}>Once per applicant</div>
                </div>
              </div>

              <p style={{color: '#475569', lineHeight: '1.8', margin: 0, fontSize: '0.95rem'}}>
                Please note: a scholarship can only be awarded <strong>once</strong> to the same applicant. Applicants are kindly asked to complete the form below fully and thoughtfully.
              </p>
            </div>
          </div>

          {/* Apply Button or Form (or Closed notice) */}
          {scholarshipSettings.scholarshipApplicationsClosed ? (
            <div style={{
              background: '#fff7ed',
              border: '1px solid #fed7aa',
              borderRadius: '12px',
              padding: '1.75rem 1.5rem',
              textAlign: 'center'
            }}>
              <div style={{fontSize: '2rem', marginBottom: '0.5rem'}}>🔒</div>
              <h3 style={{color: '#9a3412', fontSize: '1.3rem', fontWeight: 700, margin: '0 0 0.5rem'}}>
                Submissions are closed
              </h3>
              <p style={{color: '#9a3412', margin: 0, lineHeight: 1.6, maxWidth: '600px', marginLeft: 'auto', marginRight: 'auto'}}>
                {scholarshipSettings.scholarshipClosedMessage}
              </p>
            </div>
          ) : !showScholarshipForm ? (
            <div style={{textAlign: 'center'}}>
              <button
                onClick={() => setShowScholarshipForm(true)}
                style={{
                  display: 'inline-block',
                  background: '#059669',
                  color: 'white',
                  padding: '1rem 2rem',
                  borderRadius: '8px',
                  border: 'none',
                  fontWeight: '600',
                  fontSize: '1rem',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = '#047857';
                  e.target.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = '#059669';
                  e.target.style.transform = 'translateY(0)';
                }}
              >
                Apply for Scholarship →
              </button>
            </div>
          ) : (
            <div style={{
              background: '#ffffff',
              padding: '2rem',
              borderRadius: '12px',
              border: '1px solid #bbf7d0'
            }}>
              <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem'}}>
                <h3 style={{color: '#166534', fontSize: '1.4rem', fontWeight: '600', margin: 0}}>
                  Scholarship Application Form
                </h3>
                <button
                  onClick={() => setShowScholarshipForm(false)}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '1.5rem',
                    cursor: 'pointer',
                    color: '#6b7280'
                  }}
                >
                  ✕
                </button>
              </div>

              {submitStatus === 'success' && (
                <div style={{
                  background: '#dcfce7',
                  border: '1px solid #bbf7d0',
                  padding: '1rem',
                  borderRadius: '8px',
                  marginBottom: '1.5rem',
                  color: '#166534',
                  textAlign: 'center'
                }}>
                  <strong>Application submitted successfully!</strong>
                  <p style={{margin: '0.5rem 0 0 0'}}>Thank you for your application. The Conference Task Group will review it and contact you.</p>
                </div>
              )}

              {submitStatus === 'error' && (
                <div style={{
                  background: '#fef2f2',
                  border: '1px solid #fecaca',
                  padding: '1rem',
                  borderRadius: '8px',
                  marginBottom: '1.5rem',
                  color: '#dc2626',
                  textAlign: 'center'
                }}>
                  <strong>Error submitting application.</strong>
                  <p style={{margin: '0.5rem 0 0 0'}}>Please try again or contact info@ebta.eu directly.</p>
                </div>
              )}

              <form onSubmit={handleScholarshipSubmit}>
                {/* Section 1: Personal Details */}
                <div style={{marginBottom: '2rem'}}>
                  <h4 style={{color: '#166534', fontSize: '1.1rem', fontWeight: '600', marginBottom: '1rem', borderBottom: '2px solid #bbf7d0', paddingBottom: '0.5rem'}}>
                    1. Personal Details
                  </h4>

                  <div style={{marginBottom: '1rem'}}>
                    <label style={{display: 'block', marginBottom: '0.5rem', color: '#374151', fontWeight: '500'}}>
                      Full Name (Name and Surname) <span style={{color: '#dc2626'}}>*</span>
                    </label>
                    <input
                      type="text"
                      name="fullName"
                      value={scholarshipForm.fullName}
                      onChange={handleScholarshipChange}
                      required
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        borderRadius: '8px',
                        border: '1px solid #d1d5db',
                        fontSize: '1rem',
                        boxSizing: 'border-box'
                      }}
                    />
                  </div>

                  <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1rem'}}>
                    <div>
                      <label style={{display: 'block', marginBottom: '0.5rem', color: '#374151', fontWeight: '500'}}>
                        Email address <span style={{color: '#dc2626'}}>*</span>
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={scholarshipForm.email}
                        onChange={handleScholarshipChange}
                        required
                        placeholder="you@example.com"
                        style={{
                          width: '100%',
                          padding: '0.75rem',
                          borderRadius: '8px',
                          border: '1px solid #d1d5db',
                          fontSize: '1rem',
                          boxSizing: 'border-box'
                        }}
                      />
                    </div>

                    <div>
                      <label style={{display: 'block', marginBottom: '0.5rem', color: '#374151', fontWeight: '500'}}>
                        Country of Residence <span style={{color: '#dc2626'}}>*</span>
                      </label>
                      <input
                        type="text"
                        name="country"
                        value={scholarshipForm.country}
                        onChange={handleScholarshipChange}
                        required
                        style={{
                          width: '100%',
                          padding: '0.75rem',
                          borderRadius: '8px',
                          border: '1px solid #d1d5db',
                          fontSize: '1rem',
                          boxSizing: 'border-box'
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* Section 2: Professional Context */}
                <div style={{marginBottom: '2rem'}}>
                  <h4 style={{color: '#166534', fontSize: '1.1rem', fontWeight: '600', marginBottom: '1rem', borderBottom: '2px solid #bbf7d0', paddingBottom: '0.5rem'}}>
                    2. Professional Context
                  </h4>

                  <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem', marginBottom: '1rem'}}>
                    <div>
                      <label style={{display: 'block', marginBottom: '0.5rem', color: '#374151', fontWeight: '500'}}>
                        Current Workplace / Organisation (if applicable)
                      </label>
                      <input
                        type="text"
                        name="workPlace"
                        value={scholarshipForm.workPlace}
                        onChange={handleScholarshipChange}
                        style={{
                          width: '100%',
                          padding: '0.75rem',
                          borderRadius: '8px',
                          border: '1px solid #d1d5db',
                          fontSize: '1rem',
                          boxSizing: 'border-box'
                        }}
                      />
                    </div>
                    <div>
                      <label style={{display: 'block', marginBottom: '0.5rem', color: '#374151', fontWeight: '500'}}>
                        Current Role / Job Title (if applicable)
                      </label>
                      <input
                        type="text"
                        name="position"
                        value={scholarshipForm.position}
                        onChange={handleScholarshipChange}
                        style={{
                          width: '100%',
                          padding: '0.75rem',
                          borderRadius: '8px',
                          border: '1px solid #d1d5db',
                          fontSize: '1rem',
                          boxSizing: 'border-box'
                        }}
                      />
                    </div>
                  </div>

                  <div>
                    <label style={{display: 'block', marginBottom: '0.5rem', color: '#374151', fontWeight: '500'}}>
                      Area of Practice
                    </label>
                    <input
                      type="text"
                      name="areaOfPractice"
                      value={scholarshipForm.areaOfPractice}
                      onChange={handleScholarshipChange}
                      placeholder="e.g., psychotherapy, counselling, coaching, education, social services, student, other"
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        borderRadius: '8px',
                        border: '1px solid #d1d5db',
                        fontSize: '1rem',
                        boxSizing: 'border-box'
                      }}
                    />
                  </div>
                </div>

                {/* Section 3: Reasons for Applying */}
                <div style={{marginBottom: '2rem'}}>
                  <h4 style={{color: '#166534', fontSize: '1.1rem', fontWeight: '600', marginBottom: '1rem', borderBottom: '2px solid #bbf7d0', paddingBottom: '0.5rem'}}>
                    3. Reasons for Applying
                  </h4>

                  <div>
                    <label style={{display: 'block', marginBottom: '0.5rem', color: '#374151', fontWeight: '500'}}>
                      What are your good reasons for applying for a scholarship? <span style={{color: '#dc2626'}}>*</span>
                    </label>
                    <textarea
                      name="motivation"
                      value={scholarshipForm.motivation}
                      onChange={handleScholarshipChange}
                      required
                      rows={8}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        borderRadius: '8px',
                        border: '1px solid #d1d5db',
                        fontSize: '1rem',
                        boxSizing: 'border-box',
                        resize: 'vertical'
                      }}
                    />
                  </div>
                </div>

                {/* Section 4: Declaration */}
                <div style={{marginBottom: '1.5rem'}}>
                  <h4 style={{color: '#166534', fontSize: '1.1rem', fontWeight: '600', marginBottom: '1rem', borderBottom: '2px solid #bbf7d0', paddingBottom: '0.5rem'}}>
                    4. Declaration <span style={{color: '#dc2626'}}>*</span>
                  </h4>

                  <div style={{
                    background: '#f0fdf4',
                    padding: '1.5rem',
                    borderRadius: '8px',
                    border: '1px solid #bbf7d0'
                  }}>
                    <div style={{marginBottom: '1rem'}}>
                      <label style={{display: 'flex', alignItems: 'flex-start', gap: '0.75rem', cursor: 'pointer'}}>
                        <input
                          type="checkbox"
                          name="confirmAccurate"
                          checked={scholarshipForm.confirmAccurate}
                          onChange={handleScholarshipChange}
                          required
                          style={{marginTop: '0.25rem', width: '18px', height: '18px', flexShrink: 0}}
                        />
                        <span style={{color: '#166534', lineHeight: '1.5'}}>
                          I confirm that the information provided in this application is accurate and complete.
                        </span>
                      </label>
                    </div>

                    <div style={{marginBottom: '1rem'}}>
                      <label style={{display: 'flex', alignItems: 'flex-start', gap: '0.75rem', cursor: 'pointer'}}>
                        <input
                          type="checkbox"
                          name="understandNoGuarantee"
                          checked={scholarshipForm.understandNoGuarantee}
                          onChange={handleScholarshipChange}
                          required
                          style={{marginTop: '0.25rem', width: '18px', height: '18px', flexShrink: 0}}
                        />
                        <span style={{color: '#166534', lineHeight: '1.5'}}>
                          I understand that submission of this form does not guarantee the award of a scholarship.
                        </span>
                      </label>
                    </div>

                    <div>
                      <label style={{display: 'flex', alignItems: 'flex-start', gap: '0.75rem', cursor: 'pointer'}}>
                        <input
                          type="checkbox"
                          name="agreeToFeedback"
                          checked={scholarshipForm.agreeToFeedback}
                          onChange={handleScholarshipChange}
                          required
                          style={{marginTop: '0.25rem', width: '18px', height: '18px', flexShrink: 0}}
                        />
                        <span style={{color: '#166534', lineHeight: '1.5'}}>
                          I agree that if I receive this scholarship, I will provide a written feedback article about my conference experience for publication in the next EBTA newsletter.
                        </span>
                      </label>
                    </div>
                  </div>
                </div>

                <div style={{display: 'flex', gap: '1rem', justifyContent: 'flex-end'}}>
                  <button
                    type="button"
                    onClick={() => setShowScholarshipForm(false)}
                    style={{
                      padding: '0.75rem 1.5rem',
                      borderRadius: '8px',
                      border: '1px solid #d1d5db',
                      background: 'white',
                      color: '#374151',
                      fontWeight: '500',
                      fontSize: '1rem',
                      cursor: 'pointer'
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    style={{
                      padding: '0.75rem 2rem',
                      borderRadius: '8px',
                      border: 'none',
                      background: isSubmitting ? '#9ca3af' : '#059669',
                      color: 'white',
                      fontWeight: '600',
                      fontSize: '1rem',
                      cursor: isSubmitting ? 'not-allowed' : 'pointer',
                      transition: 'all 0.3s ease'
                    }}
                  >
                    {isSubmitting ? 'Submitting...' : 'Submit Application'}
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>

        {/* Organize Conference Section */}
        <div style={{
          background: 'white',
          borderRadius: '16px',
          padding: '2rem',
          marginBottom: '3rem',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
          border: '1px solid #e2e8f0'
        }}>
          <div style={{display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem'}}>
            <div style={{fontSize: '2.5rem'}}>🌟</div>
            <div>
              <h2 style={{margin: 0, color: '#1e293b', fontSize: '1.8rem'}}>Organize an EBTA Conference?</h2>
              <p style={{margin: '0.5rem 0 0 0', color: '#64748b'}}>Host a future EBTA conference</p>
            </div>
          </div>
          <div style={{marginBottom: '1.5rem'}}>
            <p style={{color: '#475569', lineHeight: '1.6', marginBottom: '1rem'}}>
              EBTA welcomes applications from solution-focused teams to host future EBTA conferences. 
              An EBTA conference usually draws about 150-250 participants.
            </p>
            <div style={{
              background: '#f0f9ff',
              padding: '1rem 1.5rem',
              borderRadius: '8px',
              border: '1px solid #bfdbfe',
              marginBottom: '1rem'
            }}>
              <strong style={{color: '#1e40af'}}>📅 Conference Schedule:</strong>
              <span style={{color: '#1e40af', marginLeft: '0.5rem'}}>
                Every year at the end of September from Friday morning 09:00 until Sunday afternoon 12:30
              </span>
            </div>
            <p style={{color: '#475569', lineHeight: '1.6'}}>
              If you're interested in organizing a future EBTA conference, please get in touch with us to discuss your proposal.
            </p>
          </div>
        </div>

        {/* Related Links */}
        <div className="research-grid" style={{marginBottom: '3rem'}}>
          <Link to="/research/grant" className="research-card">
            <div className="card-icon">🏆</div>
            <h3>EBTA Grant Awards</h3>
            <p>Learn about our research grant program and see the history of awarded grants from 2001 to 2025.</p>
            <div className="card-arrow">→</div>
          </Link>

          <Link to="/contact" className="research-card">
            <div className="card-icon">📧</div>
            <h3>Contact Us</h3>
            <p>Get in touch for more information about upcoming conferences and events.</p>
            <div className="card-arrow">→</div>
          </Link>

          <Link to="/membership" className="research-card">
            <div className="card-icon">👥</div>
            <h3>Become a Member</h3>
            <p>Join EBTA to receive priority access to conference information and member benefits.</p>
            <div className="card-arrow">→</div>
          </Link>
        </div>

        {/* Conference History Section */}
        <div style={{
          background: 'white',
          borderRadius: '16px',
          padding: '2rem',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
          border: '1px solid #e2e8f0'
        }}>
          <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem'}}>
            <div style={{display: 'flex', alignItems: 'center', gap: '1rem'}}>
              <div style={{fontSize: '2.5rem'}}>📚</div>
              <h2 style={{margin: 0, color: '#1e293b', fontSize: '1.8rem'}}>Conference History</h2>
            </div>
            <button
              onClick={() => setShowHistory(!showHistory)}
              style={{
                background: showHistory ? 'linear-gradient(135deg, #dc2626, #b91c1c)' : 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
                color: 'white',
                border: 'none',
                padding: '0.75rem 1.5rem',
                borderRadius: '8px',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              {showHistory ? '❌ Hide History' : '📖 Show History'}
            </button>
          </div>
          
          <p style={{color: '#64748b', marginBottom: showHistory ? '2rem' : 0}}>
            Explore the rich history of EBTA conferences from past years.
          </p>

          {showHistory && (
            <div style={{
              background: '#f8fafc',
              padding: '2rem',
              borderRadius: '12px',
              border: '1px solid #e2e8f0'
            }}>
              <div style={{marginBottom: '2rem'}}>
                <h3 style={{color: '#1e293b', fontSize: '1.4rem', fontWeight: '600', marginBottom: '1rem'}}>
                  📅 Conference Schedule
                </h3>
                <div style={{background: 'white', padding: '1.5rem', borderRadius: '8px', border: '1px solid #e2e8f0', marginBottom: '1.5rem'}}>
                  <div style={{marginBottom: '1rem'}}>
                    <strong style={{color: '#059669'}}>2026 – Olomouc, Czech Republic</strong>
                    <p style={{margin: '0.5rem 0', color: '#64748b'}}>
                      Theme: "Puls of Change" – Organized by Leoš Zatloukal, Palacký University in Olomouc
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <h3 style={{color: '#1e293b', fontSize: '1.4rem', fontWeight: '600', marginBottom: '1rem'}}>
                  📚 Complete Conference History (1993-2024)
                </h3>
                <div style={{background: 'white', padding: '1.5rem', borderRadius: '8px', border: '1px solid #e2e8f0'}}>
                  <div style={{display: 'grid', gap: '1rem'}}>
                    
                    <div style={{padding: '1rem', background: '#f8fafc', borderRadius: '6px', borderLeft: '3px solid #3b82f6'}}>
                      <strong style={{color: '#1e293b'}}>2025 – Roskilde, Denmark</strong>
                      <p style={{margin: '0.5rem 0', color: '#64748b'}}>
                        Theme: "In between" – Organized by Nete Fosmark Guldbrandsen, Lena Gadegaard, Michael Petersen, Kari Terndrup Hvilsom and Anne-Marie Wulf
                      </p>
                    </div>

                    <div style={{padding: '1rem', background: '#f8fafc', borderRadius: '6px', borderLeft: '3px solid #3b82f6'}}>
                      <strong style={{color: '#1e293b'}}>2024 – Tenerife, Spain</strong>
                      <p style={{margin: '0.5rem 0', color: '#64748b'}}>
                        Theme: "Ripple effect: expanding hope" – Organized by Toni Medina, Asociación para el desarrollo de la práctica centrada en soluciones
                      </p>
                    </div>

                    <div style={{padding: '1rem', background: '#f8fafc', borderRadius: '6px', borderLeft: '3px solid #3b82f6'}}>
                      <strong style={{color: '#1e293b'}}>2023 – Sosnowiec, Poland</strong>
                      <p style={{margin: '0.5rem 0', color: '#64748b'}}>
                        Theme: "Exploring resilience" – Organized by Magdalena Szutarska and Stowarzyszenie Moc Wsparcia from Sosnowiec
                      </p>
                    </div>

                    <div style={{padding: '1rem', background: '#f8fafc', borderRadius: '6px', borderLeft: '3px solid #3b82f6'}}>
                      <strong style={{color: '#1e293b'}}>2022 – Bremen, Germany</strong>
                      <p style={{margin: '0.5rem 0', color: '#64748b'}}>
                        Theme: "There's always hope" – Organized by Manfred Vogt and NIK TEAM
                      </p>
                    </div>

                    <div style={{padding: '1rem', background: '#fef2f2', borderRadius: '6px', borderLeft: '3px solid #dc2626'}}>
                      <strong style={{color: '#dc2626'}}>2020-2021 – COVID BREAK</strong>
                      <p style={{margin: '0.5rem 0', color: '#64748b'}}>
                        No conferences held due to the COVID-19 pandemic
                      </p>
                    </div>

                    <div style={{padding: '1rem', background: '#f8fafc', borderRadius: '6px', borderLeft: '3px solid #3b82f6'}}>
                      <strong style={{color: '#1e293b'}}>2019 – Florence, Italy</strong>
                      <p style={{margin: '0.5rem 0', color: '#64748b'}}>
                        Theme: "As if…" – Organized by Fondazione Franceschi Onlus
                      </p>
                    </div>

                    <div style={{padding: '1rem', background: '#f8fafc', borderRadius: '6px', borderLeft: '3px solid #3b82f6'}}>
                      <strong style={{color: '#1e293b'}}>2018 – Sofia, Bulgaria</strong>
                      <p style={{margin: '0.5rem 0', color: '#64748b'}}>
                        Theme: "21st Century Solutions, 21 years later…" – Organized by Plamen Panayotov, Boyan Strahilov
                      </p>
                    </div>

                    <div style={{padding: '1rem', background: '#fef3c7', borderRadius: '6px', borderLeft: '3px solid #f59e0b'}}>
                      <strong style={{color: '#92400e'}}>2017 – Frankfurt</strong>
                      <p style={{margin: '0.5rem 0', color: '#64748b'}}>
                        SF World Conference
                      </p>
                    </div>

                    <div style={{padding: '1rem', background: '#f8fafc', borderRadius: '6px', borderLeft: '3px solid #3b82f6'}}>
                      <strong style={{color: '#1e293b'}}>2016 – Bruges</strong>
                      <p style={{margin: '0.5rem 0', color: '#64748b'}}>
                        Theme: "The map is not the territory" – Organized by Korzybski Institute
                      </p>
                    </div>

                    <div style={{padding: '1rem', background: '#f8fafc', borderRadius: '6px', borderLeft: '3px solid #3b82f6'}}>
                      <strong style={{color: '#1e293b'}}>2015 – Vienna</strong>
                      <p style={{margin: '0.5rem 0', color: '#64748b'}}>
                        Theme: "Don't think – observe" – Organized by Veronika Kotrba, Ralph Miarka, Claudia Lembach, Ferdinand Wolf
                      </p>
                    </div>

                    <div style={{padding: '1rem', background: '#f8fafc', borderRadius: '6px', borderLeft: '3px solid #3b82f6'}}>
                      <strong style={{color: '#1e293b'}}>2014 – Leeuwarden</strong>
                      <p style={{margin: '0.5rem 0', color: '#64748b'}}>
                        Theme: "Delta - Resources United" – Organized by Ieta Berghuis, Lammie Lamberts, Rienk Versloot, Jos Kienhuis
                      </p>
                    </div>

                    <div style={{padding: '1rem', background: '#f8fafc', borderRadius: '6px', borderLeft: '3px solid #3b82f6'}}>
                      <strong style={{color: '#1e293b'}}>2013 – Bern</strong>
                      <p style={{margin: '0.5rem 0', color: '#64748b'}}>
                        Theme: "I'm my style" – Organized by The NLA Network for SF work Switzerland
                      </p>
                    </div>

                    <div style={{padding: '1rem', background: '#f8fafc', borderRadius: '6px', borderLeft: '3px solid #3b82f6'}}>
                      <strong style={{color: '#1e293b'}}>2012 – Torun</strong>
                      <p style={{margin: '0.5rem 0', color: '#64748b'}}>
                        Theme: "Upside down – SF paradigms – revolutions & evolutions" – Organized by Tomasz Switek, Artur Lewinski, Jacek Szczepkowski and volunteers from Warsaw and Toruń
                      </p>
                    </div>

                    <div style={{padding: '1rem', background: '#f8fafc', borderRadius: '6px', borderLeft: '3px solid #3b82f6'}}>
                      <strong style={{color: '#1e293b'}}>2011 – Dresden</strong>
                      <p style={{margin: '0.5rem 0', color: '#64748b'}}>
                        Theme: "Frames and beyond – How the frames influence our doing and the doing influences" – Organized by The f.l.o.w. association and NIK
                      </p>
                    </div>

                    <div style={{padding: '1rem', background: '#f8fafc', borderRadius: '6px', borderLeft: '3px solid #3b82f6'}}>
                      <strong style={{color: '#1e293b'}}>2010 – Malmö</strong>
                      <p style={{margin: '0.5rem 0', color: '#64748b'}}>
                        Theme: "Bridges" – Organized by Harry Korman – SIKT
                      </p>
                    </div>

                    <div style={{padding: '1rem', background: '#f8fafc', borderRadius: '6px', borderLeft: '3px solid #3b82f6'}}>
                      <strong style={{color: '#1e293b'}}>2009 – Helsinki</strong>
                      <p style={{margin: '0.5rem 0', color: '#64748b'}}>
                        Theme: "Imagine" – Organized by TaitoBa, Helsinki University Palmenia, Ratkes ry
                      </p>
                    </div>

                    <div style={{padding: '1rem', background: '#f8fafc', borderRadius: '6px', borderLeft: '3px solid #3b82f6'}}>
                      <strong style={{color: '#1e293b'}}>2008 – Lyon</strong>
                      <p style={{margin: '0.5rem 0', color: '#64748b'}}>
                        Theme: "Différences et changements" – Organized by Pascal Soubeyrand, Hélèn Dellucci and Association Différences et Changements
                      </p>
                    </div>

                    <div style={{padding: '1rem', background: '#f8fafc', borderRadius: '6px', borderLeft: '3px solid #3b82f6'}}>
                      <strong style={{color: '#1e293b'}}>2007 – Brugge</strong>
                      <p style={{margin: '0.5rem 0', color: '#64748b'}}>
                        Organized by Korzybski Institute
                      </p>
                    </div>

                    <div style={{padding: '1rem', background: '#f8fafc', borderRadius: '6px', borderLeft: '3px solid #3b82f6'}}>
                      <strong style={{color: '#1e293b'}}>2006 – Krakow</strong>
                      <p style={{margin: '0.5rem 0', color: '#64748b'}}>
                        Organized by Centrum Terapii Krótkoterminowej
                      </p>
                    </div>

                    <div style={{padding: '1rem', background: '#f8fafc', borderRadius: '6px', borderLeft: '3px solid #3b82f6'}}>
                      <strong style={{color: '#1e293b'}}>2005 – Salamanca</strong>
                      <p style={{margin: '0.5rem 0', color: '#64748b'}}>
                        Organized by Dr. Mark Beyebach and team
                      </p>
                    </div>

                    <div style={{padding: '1rem', background: '#f8fafc', borderRadius: '6px', borderLeft: '3px solid #3b82f6'}}>
                      <strong style={{color: '#1e293b'}}>2004 – Amsterdam</strong>
                      <p style={{margin: '0.5rem 0', color: '#64748b'}}>
                        Theme: "Leading from one step behind. What works? Our clients will tell us!" – Organized by Arnoud Huibers and team
                      </p>
                    </div>

                    <div style={{padding: '1rem', background: '#f0fdf4', borderRadius: '6px', borderLeft: '3px solid #059669'}}>
                      <strong style={{color: '#166534'}}>2003 – Berlin</strong>
                      <p style={{margin: '0.5rem 0', color: '#64748b'}}>
                        Theme: "Let's Talk About Solutions" – 10 year anniversary of EBTA Conferences – Organized by NIK
                      </p>
                    </div>

                    <div style={{padding: '1rem', background: '#f8fafc', borderRadius: '6px', borderLeft: '3px solid #3b82f6'}}>
                      <strong style={{color: '#1e293b'}}>2002 – Cardiff</strong>
                    </div>

                    <div style={{padding: '1rem', background: '#f8fafc', borderRadius: '6px', borderLeft: '3px solid #3b82f6'}}>
                      <strong style={{color: '#1e293b'}}>2001 – Dublin</strong>
                    </div>

                    <div style={{padding: '1rem', background: '#f8fafc', borderRadius: '6px', borderLeft: '3px solid #3b82f6'}}>
                      <strong style={{color: '#1e293b'}}>2000 – Turku</strong>
                      <p style={{margin: '0.5rem 0', color: '#64748b'}}>
                        Organized by Turku Summer University
                      </p>
                    </div>

                    <div style={{padding: '1rem', background: '#f8fafc', borderRadius: '6px', borderLeft: '3px solid #3b82f6'}}>
                      <strong style={{color: '#1e293b'}}>1999 – Carlisle</strong>
                      <p style={{margin: '0.5rem 0', color: '#64748b'}}>
                        Theme: "Solutions not problems" – Organized by Dr Alasdair Macdonald and team
                      </p>
                    </div>

                    <div style={{padding: '1rem', background: '#f8fafc', borderRadius: '6px', borderLeft: '3px solid #3b82f6'}}>
                      <strong style={{color: '#1e293b'}}>1998 – Salamanca</strong>
                      <p style={{margin: '0.5rem 0', color: '#64748b'}}>
                        Organized by Mark Beyebach and team
                      </p>
                    </div>

                    <div style={{padding: '1rem', background: '#f8fafc', borderRadius: '6px', borderLeft: '3px solid #3b82f6'}}>
                      <strong style={{color: '#1e293b'}}>1997 – Sofia (Spring) and Bruges (Autumn)</strong>
                    </div>

                    <div style={{padding: '1rem', background: '#f8fafc', borderRadius: '6px', borderLeft: '3px solid #3b82f6'}}>
                      <strong style={{color: '#1e293b'}}>1996 – Paris (Spring) and Lodz (Autumn)</strong>
                    </div>

                    <div style={{padding: '1rem', background: '#f8fafc', borderRadius: '6px', borderLeft: '3px solid #3b82f6'}}>
                      <strong style={{color: '#1e293b'}}>1995 – London (Spring) and Bremen (Autumn)</strong>
                    </div>

                    <div style={{padding: '1rem', background: '#f8fafc', borderRadius: '6px', borderLeft: '3px solid #3b82f6'}}>
                      <strong style={{color: '#1e293b'}}>1994 – Bruges (Spring) and Stockholm (Autumn)</strong>
                    </div>

                    <div style={{padding: '1rem', background: '#fef3c7', borderRadius: '6px', borderLeft: '3px solid #f59e0b'}}>
                      <strong style={{color: '#92400e'}}>1993 – New Orleans and Paris</strong>
                      <p style={{margin: '0.5rem 0', color: '#64748b'}}>
                        🎉 <strong>EBTA was founded at the New Orleans Conference in honor of John Weakland in 1993</strong>
                      </p>
                    </div>

                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Conferences;
